import { WalletContextState } from '@solana/wallet-adapter-react';
import { buildReleaseNftMetadata } from '../../../api/src/shared/nft/releaseMetadataBuilder';
import { canonicalizeArweaveGatewayUrl } from '../shared/arweave/publicArweaveGateway';
import { NFT_SCHEMA_VERSION } from '../constants/nft';
import { TRPC } from '../contexts/TRPC';
import { isLeftResult } from '../lib/trpcTypeGuards';
import { calculateFileHash } from './crypto';
import { getErrorMessage } from './errorHandling';
import { addBreadcrumb, captureAndReportError } from './errorReporting';
import { fetchAndConvertToFile } from './fetchAndConvertToFile';
import {
  getImageOnlyDimensions,
  getMediaDimensions,
  isVideoMimeType,
} from './imageDimensions';
import { ensureMediaDecodable } from './mediaValidation';
import { ensureHttpsUrl, ensureIconInMediaArray } from './nftMetadataHelpers';
import { getReusableR2ReleaseApk } from './nftMintUtils';
import {
  providerRequiresWallet,
  type UploadProviderPreference,
} from './providerHelpers';
import { buildReleaseDisplayName } from './releaseNftName';
import { releaseDebugLog } from './releaseDebugLogger';
import {
  type ProviderConfig,
  type UploadOptions,
  type UploadProgress,
  type UploadProvider,
  UploadProviderFactory,
} from './upload-providers';
import { createWalletAdapter } from './wallet';

export type { UploadProgress } from './upload-providers';

export interface AppMetadata {
  name: string;
  website?: string;
  androidPackage: string;
  address: string;
}

interface ReleaseMediaItem {
  mime: string;
  purpose: string;
  uri: string;
  size?: number;
  width?: number;
  height?: number;
  sha256?: string;
  [key: string]: string | number | undefined;
}

type TRPCClient = TRPC['client'];

function canonicalizeProviderUri(
  uri: string,
  provider: 'Arweave' | 'S3' | 'R2',
): string {
  return provider === 'Arweave' ? canonicalizeArweaveGatewayUrl(uri) : uri;
}

function mapProviderToDatabase(provider: UploadProviderPreference): {
  provider: 'Arweave' | 'S3' | 'R2';
  facilitator?: 'ArDrive' | 'S3' | 'CloudflareR2';
} {
  switch (provider) {
    case 'ardrive':
      return { provider: 'Arweave', facilitator: 'ArDrive' };
    case 's3':
      return { provider: 'S3', facilitator: 'S3' };
    case 'r2':
      return { provider: 'R2', facilitator: 'CloudflareR2' };
    default:
      return { provider: 'Arweave', facilitator: 'ArDrive' };
  }
}

function extractProviderId(url: string, provider: string): string {
  if (provider === 's3' || provider === 'r2') {
    const urlParts = new URL(url);
    return urlParts.pathname.substring(1);
  } else {
    const match = url.match(/\/([a-zA-Z0-9_-]+)$/);
    return match ? match[1] : url;
  }
}

async function fetchFileForUpload(
  url: string,
  defaultFileName: string,
  trpc: TRPCClient,
): Promise<File> {
  const file = await fetchAndConvertToFile(url, trpc, {
    fileName: defaultFileName,
  });

  if (!file.name.includes('.') && file.type) {
    const extension = file.type.split('/')[1]?.split(';')[0];
    if (extension) {
      const buffer = await file.arrayBuffer();
      return new File([buffer], `${file.name}.${extension}`, {
        type: file.type,
      });
    }
  }

  return file;
}

async function isRemoteAssetAccessible(url: string): Promise<boolean> {
  try {
    const response = await fetch(url, { method: 'HEAD' });
    if (response.ok) {
      return true;
    }
    console.warn(
      `HEAD request for ${url} returned status ${response.status}, falling back to re-upload`,
    );
    return false;
  } catch (error) {
    console.warn(
      `Failed to confirm remote asset accessibility for ${url}, will re-upload`,
      error,
    );
    return false;
  }
}

async function uploadAssetWithDedup(
  file: File | Blob,
  fileType:
    | 'PublisherIcon'
    | 'AppIcon'
    | 'AppMetadata'
    | 'ReleaseMetadata'
    | 'ReleaseAPK'
    | 'ReleaseIcon'
    | 'ReleaseScreenshot'
    | 'ReleaseBanner'
    | 'ReleaseFeatureGraphic',
  tags: Array<{ name: string; value: string }>,
  provider: UploadProvider,
  dbProviderInfo: {
    provider: 'Arweave' | 'S3' | 'R2';
    facilitator?: 'ArDrive' | 'S3' | 'CloudflareR2';
  },
  trpc: TRPCClient | undefined,
  preferences: { uploadProvider: UploadProviderPreference },
  onProgress?: (progress: UploadProgress) => void,
  uploadContext?: UploadOptions['context'],
): Promise<{
  uri: string;
  hash: string;
  providerId: string;
  dimensions?: { width?: number; height?: number };
}> {
  const fileSize = (file as Blob).size ?? 0;
  if (fileSize <= 0) {
    throw new Error(
      `Cannot upload ${fileType}: the file is empty. Please re-download the asset before retrying.`,
    );
  }

  await ensureMediaDecodable(file, file.type, fileType);

  const fileHash = await calculateFileHash(file);

  if (trpc) {
    try {
      releaseDebugLog(
        `Checking deduplication for ${fileType}, hash: ${fileHash}, provider: ${dbProviderInfo.provider}`,
      );
      const dedupResult = await trpc.arweave.checkFile.mutate({
        fileHash,
        fileType,
        provider: dbProviderInfo.provider,
        purpose: 'NftMetadata',
      });

      if (isLeftResult(dedupResult)) {
        captureAndReportError(dedupResult.left, {
          operation: 'Deduplication check error',
          component: 'arweaveUploadProviderBased',
          fileType,
        });
      } else if (dedupResult.right.exists && dedupResult.right.providerUri) {
        const reuseUri = canonicalizeProviderUri(
          dedupResult.right.providerUri,
          dbProviderInfo.provider,
        );
        if (await isRemoteAssetAccessible(reuseUri)) {
          releaseDebugLog(
            `✅ Deduplication HIT! Reusing existing ${fileType}: ${reuseUri}`,
          );
          return {
            uri: reuseUri,
            hash: fileHash,
            providerId:
              dedupResult.right.providerId ||
              extractProviderId(reuseUri, preferences.uploadProvider),
            dimensions: undefined,
          };
        }

        console.warn(
          `⚠️ Cached ${fileType} at ${reuseUri} is not accessible. Re-uploading.`,
        );
      } else {
        releaseDebugLog(
          `❌ Deduplication MISS for ${fileType}, will upload new file`,
        );
      }
    } catch (error) {
      console.warn(
        'Deduplication check failed, proceeding with upload:',
        error,
      );
    }
  }

  let dimensions: { width?: number; height?: number } = {};
  const mimeType = file.type;
  const isVideo = isVideoMimeType(mimeType);
  const isImage = mimeType.startsWith('image/');

  if (isImage || isVideo) {
    try {
      dimensions = await getMediaDimensions(file, mimeType);
      releaseDebugLog(
        `Extracted ${isVideo ? 'video' : 'image'} dimensions for ${fileType}:`,
        dimensions,
      );
    } catch (error) {
      console.warn(
        `Failed to extract ${isVideo ? 'video' : 'image'} dimensions:`,
        error,
      );
    }
  }

  const buffer = Buffer.from(await file.arrayBuffer());
  const result = await provider.uploadFromBuffer(buffer, {
    contentType: (file as File).type || 'application/octet-stream',
    context: {
      ...uploadContext,
      nftFileType: fileType,
    },
    tags,
    onProgress,
  });
  const providerUri = canonicalizeProviderUri(
    result.url,
    dbProviderInfo.provider,
  );
  const providerId =
    result.id || extractProviderId(providerUri, preferences.uploadProvider);

  if (trpc) {
    try {
      const recordResult = await trpc.arweave.recordUpload.mutate({
        fileHash,
        fileType,
        fileSize: buffer.length,
        mimeType: (file as File).type || 'application/octet-stream',
        provider: dbProviderInfo.provider,
        facilitator: dbProviderInfo.facilitator,
        providerId,
        providerUri,
        metadata: dimensions,
        purpose: 'NftMetadata',
      });

      if (isLeftResult(recordResult)) {
        captureAndReportError(recordResult.left, {
          operation: 'Failed to record upload',
          component: 'arweaveUploadProviderBased',
          fileType,
        });
      } else {
        releaseDebugLog(
          `📝 Successfully recorded ${fileType} upload for future deduplication - hash: ${fileHash}, provider: ${dbProviderInfo.provider}, facilitator: ${dbProviderInfo.facilitator || 'none'}, url: ${providerUri}`,
        );
      }
    } catch (error) {
      console.warn('Failed to record upload for deduplication:', error);
    }
  }

  return { uri: providerUri, hash: fileHash, providerId, dimensions };
}

export async function uploadAppNftDataV2(
  wallet: WalletContextState,
  uploadProvider: UploadProviderPreference,
  appData: {
    name: string;
    website?: string;
    androidPackage: string;
    address: string;
  },
  iconFile: File,
  trpc?: TRPCClient,
  onProgress?: (
    progress: number,
    message?: string,
    isSignatureInProgress?: boolean,
  ) => void,
): Promise<{ metadataUri: string; imageUri: string }> {
  const walletAdapter = providerRequiresWallet(uploadProvider)
    ? createWalletAdapter(wallet, {
        throwOnInvalid: false,
        includeSendTransaction: false,
        includeTransactionSigning: true,
        normalizeSignature: true,
        onSignMessageRequested: () => {
          releaseDebugLog('Wallet signature requested');
        },
      })
    : undefined;

  if (providerRequiresWallet(uploadProvider) && !walletAdapter) {
    throw new Error('Wallet does not support message signing');
  }

  const config: ProviderConfig = {
    walletAdapter,
    trpcClient: trpc,
  };
  const provider = await UploadProviderFactory.getProvider(
    uploadProvider,
    config,
  );
  const providerMapping = mapProviderToDatabase(uploadProvider);

  releaseDebugLog(`=== Starting App NFT Upload with ${uploadProvider} ===`);

  addBreadcrumb('App NFT upload starting', 'nft-mint', {
    appName: appData.name,
    androidPackage: appData.androidPackage,
    iconSize: iconFile.size,
    iconType: iconFile.type,
    provider: uploadProvider,
  });

  let iconUri: string;
  try {
    if (onProgress) {
      onProgress(10, 'Uploading app icon...');
    }

    const iconResult = await uploadAssetWithDedup(
      iconFile,
      'AppIcon',
      [
        { name: 'Type', value: 'app-icon' },
        { name: 'App-Name', value: appData.name },
      ],
      provider,
      providerMapping,
      trpc,
      { uploadProvider },
      onProgress
        ? (progress) => {
            onProgress(
              10 + Math.round(progress.percentage * 0.4),
              'Uploading app icon...',
              progress.isSignatureInProgress,
            );
          }
        : undefined,
    );

    iconUri = iconResult.uri;
  } catch (error) {
    captureAndReportError(error, {
      operation: 'uploadAppNftDataV2.iconUpload',
      component: 'arweaveUploadProviderBased',
      appName: appData.name,
      androidPackage: appData.androidPackage,
      iconSize: iconFile.size,
      iconType: iconFile.type,
      provider: uploadProvider,
    });
    throw new Error(`Failed to upload app icon: ${getErrorMessage(error)}`);
  }

  const metadata = {
    schema_version: NFT_SCHEMA_VERSION,
    name: appData.name,
    description: `${appData.name} - A Solana Mobile dApp`,
    image: iconUri,
    ...(appData.website ? { external_url: appData.website } : {}),
    attributes: [
      { trait_type: 'Type', value: 'App' },
      { trait_type: 'Publisher', value: appData.address },
      { trait_type: 'Android Package', value: appData.androidPackage },
    ],
    properties: {
      files: [
        {
          uri: iconUri,
          type: 'image/png',
        },
      ],
      category: 'dApp',
      creators: [
        {
          address: appData.address,
          share: 100,
        },
      ],
    },
    extensions: {
      solana_dapp_store: {
        android_package: appData.androidPackage,
      },
    },
  };

  if (onProgress) {
    onProgress(50, 'Uploading app metadata...');
  }

  addBreadcrumb('Metadata upload starting', 'nft-mint', {
    appName: appData.name,
    metadataSize: JSON.stringify(metadata).length,
    iconUri: iconUri?.substring(0, 50),
  });

  const metadataFile = new File(
    [JSON.stringify(metadata)],
    'app-metadata.json',
    {
      type: 'application/json',
    },
  );
  const metadataResult = await uploadAssetWithDedup(
    metadataFile,
    'AppMetadata',
    [
      { name: 'App-Name', value: 'solana-mobile-dapp-publisher-portal' },
      { name: 'App-Version', value: '1.0.0' },
      { name: 'Content-Type', value: 'application/json' },
      { name: 'Type', value: 'app-nft-metadata' },
      { name: 'Dapp-Name', value: appData.name },
      { name: 'Android-Package', value: appData.androidPackage },
    ],
    provider,
    providerMapping,
    trpc,
    { uploadProvider },
    onProgress
      ? (progress) => {
          onProgress(
            50 + Math.round(progress.percentage * 0.5),
            'Uploading app metadata...',
            progress.isSignatureInProgress,
          );
        }
      : undefined,
  );
  const metadataUri = metadataResult.uri;

  releaseDebugLog('App NFT metadata uploaded:', metadataUri);

  return { metadataUri, imageUri: iconUri };
}

export async function uploadReleaseNftDataV2(
  wallet: WalletContextState,
  uploadProvider: UploadProviderPreference,
  releaseData: {
    releaseId?: string;
    releaseName: string;
    releaseVersion: string;
    androidPackage: string;
    versionCode: number;
    permissions: string[];
    minSdk: number;
    targetSdk: number | null;
    certificateFingerprint: string;
    publisherAddress: string;
    apkFile?: File;
    existingApk?: {
      uri: string;
      size: number;
      sha256: string;
    };
    apkFileHash: string;
    iconFile?: File;
    screenshotFiles?: File[];
    bannerFile?: File;
    featureGraphicFile?: File;
    publisherName: string;
    publisherWebsite: string;
    publisherEmail: string;
    publisherSupportEmail: string;
    licenseUrl: string;
    copyrightUrl: string;
    privacyPolicyUrl: string;
    shortDescription: string;
    longDescription: string;
    whatsNew: string;
    localizedName: string;
    locales: string[];
    existingIconUrl?: string;
    existingScreenshotUrls?: string[];
    existingBannerUrl?: string;
    existingFeatureGraphicUrl?: string;
  },
  trpc: TRPCClient,
  onProgress?: (
    progress: number,
    message?: string,
    isSignatureInProgress?: boolean,
  ) => void,
): Promise<{
  metadataUri: string;
  apkUri: string;
  iconUri?: string;
  screenshotUris: string[];
  bannerUri?: string;
  uploadProvider?: 'Arweave' | 'S3' | 'R2';
  uploadProviderId?: string;
}> {
  const walletAdapter = providerRequiresWallet(uploadProvider)
    ? createWalletAdapter(wallet, {
        throwOnInvalid: false,
        includeSendTransaction: false,
        includeTransactionSigning: true,
        normalizeSignature: true,
        onSignMessageRequested: () => {
          releaseDebugLog('Wallet signature requested');
        },
      })
    : undefined;

  if (providerRequiresWallet(uploadProvider) && !walletAdapter) {
    throw new Error('Wallet does not support message signing');
  }

  const config: ProviderConfig = {
    walletAdapter,
    trpcClient: trpc,
  };
  const provider = await UploadProviderFactory.getProvider(
    uploadProvider,
    config,
  );

  releaseDebugLog(`=== Starting Release NFT Upload with ${uploadProvider} ===`);
  const reusableApk = releaseData.existingApk
    ? getReusableR2ReleaseApk({
        uploadProvider,
        sourceUrl: releaseData.existingApk.uri,
        fileSize: releaseData.existingApk.size,
        fileHash: releaseData.existingApk.sha256,
      })
    : null;

  if (!releaseData.apkFile && !reusableApk) {
    throw new Error('A valid APK file or existing R2 APK is required');
  }

  const apkSize = releaseData.apkFile?.size ?? reusableApk?.size ?? 0;
  releaseDebugLog('APK size:', apkSize, 'bytes');

  let totalSize = (releaseData.apkFile ? apkSize : 0) + 1000;
  if (releaseData.iconFile) totalSize += releaseData.iconFile.size;
  if (releaseData.screenshotFiles) {
    releaseData.screenshotFiles.forEach((f) => {
      totalSize += f.size;
    });
  }
  if (releaseData.bannerFile) totalSize += releaseData.bannerFile.size;
  if (releaseData.featureGraphicFile)
    totalSize += releaseData.featureGraphicFile.size;

  let uploadedSize = 0;

  const updateProgressBySize = (completedSize: number, message: string) => {
    uploadedSize += completedSize;
    const progress = Math.min(99, Math.round((uploadedSize / totalSize) * 100));
    if (onProgress) {
      onProgress(progress, message);
    }
  };

  if (onProgress) {
    onProgress(5, 'Starting APK upload...');
  }

  const uploadContext = releaseData.releaseId
    ? { releaseId: releaseData.releaseId }
    : undefined;

  const providerMapping = mapProviderToDatabase(uploadProvider);
  let apkUri: string;
  let actualApkHash: string;

  if (reusableApk) {
    apkUri = reusableApk.uri;
    actualApkHash = reusableApk.sha256;
    onProgress?.(5, 'Using existing validated APK...');
  } else {
    const apkResult = await uploadAssetWithDedup(
      releaseData.apkFile!,
      'ReleaseAPK',
      [
        { name: 'Type', value: 'release-apk' },
        { name: 'App-Name', value: releaseData.releaseName },
        { name: 'Version', value: releaseData.releaseVersion },
        { name: 'Version-Code', value: releaseData.versionCode.toString() },
        { name: 'Android-Package', value: releaseData.androidPackage },
      ],
      provider,
      providerMapping,
      trpc,
      { uploadProvider },
      (progress) => {
        if (onProgress) {
          const overallProgress = 5 + Math.round(progress.percentage * 0.5);
          onProgress(
            overallProgress,
            progress.message || 'Uploading APK...',
            progress.isSignatureInProgress,
          );
        }
      },
      uploadContext,
    );
    apkUri = apkResult.uri;
    actualApkHash = apkResult.hash;
    updateProgressBySize(apkSize, 'APK uploaded');
  }

  const mediaArray: ReleaseMediaItem[] = [];
  let iconUri: string | undefined;
  let bannerUri: string | undefined;
  const screenshotUris: string[] = [];

  let iconHash: string | undefined;
  if (releaseData.iconFile) {
    if (onProgress) {
      onProgress(
        Math.round((uploadedSize / totalSize) * 100),
        'Uploading app icon...',
      );
    }

    const iconResult = await uploadAssetWithDedup(
      releaseData.iconFile,
      'ReleaseIcon',
      [
        { name: 'Type', value: 'release-icon' },
        { name: 'App-Name', value: releaseData.releaseName },
      ],
      provider,
      providerMapping,
      trpc,
      { uploadProvider },
      undefined,
      uploadContext,
    );

    iconUri = iconResult.uri;
    iconHash = iconResult.hash;
    mediaArray.push({
      mime: 'image/png',
      purpose: 'icon',
      uri: iconUri,
      size: releaseData.iconFile.size,
      width: iconResult.dimensions?.width || 512,
      height: iconResult.dimensions?.height || 512,
      sha256: iconHash,
    });

    updateProgressBySize(releaseData.iconFile.size, 'Icon uploaded');
  } else if (releaseData.existingIconUrl) {
    try {
      if (onProgress) {
        onProgress(
          Math.round((uploadedSize / totalSize) * 100),
          'Uploading existing icon...',
        );
      }

      const iconFile = await fetchFileForUpload(
        releaseData.existingIconUrl,
        'release-icon',
        trpc,
      );

      const iconResult = await uploadAssetWithDedup(
        iconFile,
        'ReleaseIcon',
        [
          { name: 'Type', value: 'release-icon' },
          { name: 'App-Name', value: releaseData.releaseName },
        ],
        provider,
        providerMapping,
        trpc,
        { uploadProvider },
        undefined,
        uploadContext,
      );

      iconUri = iconResult.uri;
      const iconDimensions =
        iconResult.dimensions || (await getImageOnlyDimensions(iconFile));

      mediaArray.push({
        mime: iconFile.type || 'image/png',
        purpose: 'icon',
        uri: iconUri,
        size: iconFile.size,
        width: iconDimensions?.width || 0,
        height: iconDimensions?.height || 0,
        sha256: iconResult.hash,
      });

      updateProgressBySize(iconFile.size, 'Existing icon uploaded');
    } catch (error) {
      const message = getErrorMessage(error);
      throw new Error(`Failed to process existing icon: ${message}`);
    }
  }

  if (releaseData.screenshotFiles && releaseData.screenshotFiles.length > 0) {
    for (let i = 0; i < releaseData.screenshotFiles.length; i++) {
      const file = releaseData.screenshotFiles[i];
      if (onProgress) {
        onProgress(
          Math.round((uploadedSize / totalSize) * 100),
          `Uploading screenshot ${i + 1}/${releaseData.screenshotFiles.length}...`,
        );
      }

      const screenshotResult = await uploadAssetWithDedup(
        file,
        'ReleaseScreenshot',
        [
          { name: 'Type', value: 'release-screenshot' },
          { name: 'App-Name', value: releaseData.releaseName },
          { name: 'Index', value: i.toString() },
        ],
        provider,
        providerMapping,
        trpc,
        { uploadProvider },
        undefined,
        uploadContext,
      );

      screenshotUris.push(screenshotResult.uri);

      const isVideo = isVideoMimeType(file.type);
      mediaArray.push({
        mime: file.type || (isVideo ? 'video/mp4' : 'image/png'),
        purpose: 'screenshot',
        uri: screenshotResult.uri,
        size: file.size,
        width: screenshotResult.dimensions?.width || (isVideo ? 1920 : 1080),
        height: screenshotResult.dimensions?.height || (isVideo ? 1080 : 1920),
        sha256: screenshotResult.hash,
      });

      updateProgressBySize(file.size, `Screenshot ${i + 1} uploaded`);
    }
  } else if (releaseData.existingScreenshotUrls) {
    for (let i = 0; i < releaseData.existingScreenshotUrls.length; i++) {
      const url = releaseData.existingScreenshotUrls[i];
      try {
        if (onProgress) {
          onProgress(
            Math.round((uploadedSize / totalSize) * 100),
            `Uploading existing screenshot ${i + 1}...`,
          );
        }

        const screenshotFile = await fetchFileForUpload(
          url,
          `release-screenshot-${i + 1}`,
          trpc,
        );

        const isVideo = isVideoMimeType(screenshotFile.type);
        const screenshotResult = await uploadAssetWithDedup(
          screenshotFile,
          'ReleaseScreenshot',
          [
            { name: 'Type', value: 'release-screenshot' },
            { name: 'App-Name', value: releaseData.releaseName },
            { name: 'Index', value: i.toString() },
          ],
          provider,
          providerMapping,
          trpc,
          { uploadProvider },
          undefined,
          uploadContext,
        );

        const dimensions =
          screenshotResult.dimensions ||
          (await getMediaDimensions(screenshotFile));

        screenshotUris.push(screenshotResult.uri);

        mediaArray.push({
          mime: screenshotFile.type || (isVideo ? 'video/mp4' : 'image/png'),
          purpose: 'screenshot',
          uri: screenshotResult.uri,
          size: screenshotFile.size,
          width: dimensions?.width || (isVideo ? 1920 : 1080),
          height: dimensions?.height || (isVideo ? 1080 : 1920),
          sha256: screenshotResult.hash,
        });

        updateProgressBySize(
          screenshotFile.size,
          `Existing screenshot ${i + 1} uploaded`,
        );
      } catch (error) {
        const message = getErrorMessage(error);
        throw new Error(
          `Failed to process existing screenshot ${i + 1}: ${message}`,
        );
      }
    }
  }

  if (releaseData.bannerFile) {
    if (onProgress) {
      onProgress(
        Math.round((uploadedSize / totalSize) * 100),
        'Uploading banner...',
      );
    }

    const bannerResult = await uploadAssetWithDedup(
      releaseData.bannerFile,
      'ReleaseBanner',
      [
        { name: 'Type', value: 'release-banner' },
        { name: 'App-Name', value: releaseData.releaseName },
      ],
      provider,
      providerMapping,
      trpc,
      { uploadProvider },
      undefined,
      uploadContext,
    );

    bannerUri = bannerResult.uri;
    mediaArray.push({
      mime: 'image/png',
      purpose: 'banner',
      uri: bannerResult.uri,
      size: releaseData.bannerFile.size,
      width: bannerResult.dimensions?.width || 1024,
      height: bannerResult.dimensions?.height || 500,
      sha256: bannerResult.hash,
    });

    updateProgressBySize(releaseData.bannerFile.size, 'Banner uploaded');
  } else if (releaseData.existingBannerUrl) {
    try {
      const bannerFile = await fetchFileForUpload(
        releaseData.existingBannerUrl,
        'release-banner',
        trpc,
      );

      const bannerResult = await uploadAssetWithDedup(
        bannerFile,
        'ReleaseBanner',
        [
          { name: 'Type', value: 'release-banner' },
          { name: 'App-Name', value: releaseData.releaseName },
        ],
        provider,
        providerMapping,
        trpc,
        { uploadProvider },
        undefined,
        uploadContext,
      );

      const dimensions =
        bannerResult.dimensions || (await getImageOnlyDimensions(bannerFile));

      bannerUri = bannerResult.uri;
      mediaArray.push({
        mime: bannerFile.type || 'image/png',
        purpose: 'banner',
        uri: bannerUri,
        size: bannerFile.size,
        width: dimensions?.width || 0,
        height: dimensions?.height || 0,
        sha256: bannerResult.hash,
      });

      updateProgressBySize(bannerFile.size, 'Existing banner uploaded');
    } catch (error) {
      const message = getErrorMessage(error);
      throw new Error(`Failed to process existing banner: ${message}`);
    }
  }

  let featureGraphicUri: string | undefined;
  if (releaseData.featureGraphicFile) {
    if (onProgress) {
      onProgress(
        Math.round((uploadedSize / totalSize) * 100),
        'Uploading feature graphic...',
      );
    }

    const featureResult = await uploadAssetWithDedup(
      releaseData.featureGraphicFile,
      'ReleaseFeatureGraphic',
      [
        { name: 'Type', value: 'release-feature-graphic' },
        { name: 'App-Name', value: releaseData.releaseName },
      ],
      provider,
      providerMapping,
      trpc,
      { uploadProvider },
      undefined,
      uploadContext,
    );

    featureGraphicUri = featureResult.uri;
    mediaArray.push({
      mime: 'image/png',
      purpose: 'featureGraphic',
      uri: featureResult.uri,
      size: releaseData.featureGraphicFile.size,
      width: featureResult.dimensions?.width || 1024,
      height: featureResult.dimensions?.height || 500,
      sha256: featureResult.hash,
    });

    updateProgressBySize(
      releaseData.featureGraphicFile.size,
      'Feature graphic uploaded',
    );
  } else if (releaseData.existingFeatureGraphicUrl) {
    try {
      const featureGraphicFile = await fetchFileForUpload(
        releaseData.existingFeatureGraphicUrl,
        'release-feature-graphic',
        trpc,
      );

      const featureResult = await uploadAssetWithDedup(
        featureGraphicFile,
        'ReleaseFeatureGraphic',
        [
          { name: 'Type', value: 'release-feature-graphic' },
          { name: 'App-Name', value: releaseData.releaseName },
        ],
        provider,
        providerMapping,
        trpc,
        { uploadProvider },
        undefined,
        uploadContext,
      );

      const dimensions =
        featureResult.dimensions ||
        (await getImageOnlyDimensions(featureGraphicFile));

      featureGraphicUri = featureResult.uri;
      mediaArray.push({
        mime: featureGraphicFile.type || 'image/png',
        purpose: 'featureGraphic',
        uri: featureGraphicUri,
        size: featureGraphicFile.size,
        width: dimensions?.width || 0,
        height: dimensions?.height || 0,
        sha256: featureResult.hash,
      });

      updateProgressBySize(
        featureGraphicFile.size,
        'Existing feature graphic uploaded',
      );
    } catch (error) {
      const message = getErrorMessage(error);
      throw new Error(`Failed to process existing feature graphic: ${message}`);
    }
  }

  ensureIconInMediaArray(mediaArray, iconUri);

  if (!iconUri) {
    throw new Error(
      'Release icon upload failed. Cannot mint NFT without icon.',
    );
  }

  const localizedResourceIds = {
    long_description: '1',
    new_in_version: '2',
    short_description: '5',
    name: '4',
  };

  const releaseDisplayName = buildReleaseDisplayName({
    localizedName: releaseData.localizedName,
  });

  const i18nData: Record<string, Record<string, string>> = {
    'en-US': {
      '1': releaseData.longDescription || releaseData.shortDescription || '',
      '2': releaseData.whatsNew || '',
      '4': releaseDisplayName,
      '5': releaseData.shortDescription || '',
    },
  };

  const publisherContactEmail = releaseData.publisherEmail?.trim();
  const publisherSupportEmail =
    releaseData.publisherSupportEmail?.trim() || publisherContactEmail;

  if (!publisherContactEmail) {
    throw new Error(
      'Publisher contact email is required to create release metadata.',
    );
  }

  if (!publisherSupportEmail) {
    throw new Error(
      'Publisher support email is required to create release metadata.',
    );
  }

  const normalizedPublisherWebsite = releaseData.publisherWebsite?.trim()
    ? ensureHttpsUrl(releaseData.publisherWebsite)
    : null;
  const normalizedLicenseUrl = releaseData.licenseUrl?.trim()
    ? ensureHttpsUrl(releaseData.licenseUrl)
    : null;
  const normalizedPrivacyPolicyUrl = releaseData.privacyPolicyUrl?.trim()
    ? ensureHttpsUrl(releaseData.privacyPolicyUrl)
    : null;
  const normalizedExternalUrl = releaseData.publisherWebsite?.trim()
    ? ensureHttpsUrl(releaseData.publisherWebsite)
    : null;
  const normalizedCopyrightUrl = '';

  const nftMetadata = buildReleaseNftMetadata({
    schemaVersion: NFT_SCHEMA_VERSION,
    name: releaseDisplayName,
    description: releaseData.shortDescription || 'Release NFT',
    image: iconUri,
    externalUrl: normalizedExternalUrl,
    creators: [
      {
        address: releaseData.publisherAddress,
        share: 100,
      },
    ],
    publisherDetails: {
      name: releaseData.publisherName,
      website: normalizedPublisherWebsite,
      contact: publisherContactEmail,
      support_email: publisherSupportEmail,
    },
    releaseDetails: {
      updated_on: new Date().toISOString(),
      license_url: normalizedLicenseUrl,
      copyright_url: normalizedCopyrightUrl,
      privacy_policy_url: normalizedPrivacyPolicyUrl,
      localized_resources: localizedResourceIds,
    },
    androidDetails: {
      android_package: releaseData.androidPackage,
      version: releaseData.releaseVersion,
      version_code: releaseData.versionCode,
      min_sdk: releaseData.minSdk,
      target_sdk: releaseData.targetSdk ?? null,
      cert_fingerprint: releaseData.certificateFingerprint,
      permissions: releaseData.permissions || [],
      locales: releaseData.locales || ['en-US'],
    },
    media: mediaArray,
    files: [
      {
        mime: 'application/vnd.android.package-archive',
        purpose: 'install',
        uri: apkUri,
        size: apkSize,
        sha256: actualApkHash,
      },
    ],
    androidDetailsBeforeMedia: true,
    i18n: i18nData,
  });

  if (onProgress) {
    onProgress(90, 'Uploading release metadata...');
  }

  const metadataFile = new File(
    [JSON.stringify(nftMetadata)],
    'release-metadata.json',
    {
      type: 'application/json',
    },
  );
  const metadataResult = await uploadAssetWithDedup(
    metadataFile,
    'ReleaseMetadata',
    [
      { name: 'App-Name', value: 'solana-mobile-dapp-publisher-portal' },
      { name: 'App-Version', value: '1.0.0' },
      { name: 'Content-Type', value: 'application/json' },
      { name: 'Type', value: 'release-nft-metadata' },
      { name: 'Release-Name', value: releaseData.releaseName },
      { name: 'Release-Version', value: releaseData.releaseVersion },
      { name: 'Android-Package', value: releaseData.androidPackage },
    ],
    provider,
    providerMapping,
    trpc,
    { uploadProvider },
    onProgress
      ? (progress) => {
          onProgress(
            90 + Math.round(progress.percentage * 0.1),
            'Uploading release metadata...',
            progress.isSignatureInProgress,
          );
        }
      : undefined,
    uploadContext,
  );
  const metadataUri = metadataResult.uri;

  if (onProgress) {
    onProgress(100, 'Upload complete!');
  }

  releaseDebugLog('Release NFT metadata uploaded:', metadataUri);

  return {
    metadataUri,
    apkUri: apkUri,
    iconUri: iconUri,
    screenshotUris: screenshotUris,
    bannerUri: bannerUri,
    uploadProvider: providerMapping.provider,
    uploadProviderId: metadataResult.providerId,
  };
}
